import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Box,
  Backdrop,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { CitationItem } from "../../components/citation/CitationItem";
import SearchIcon from "@mui/icons-material/Search";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";

const CitationPage = () => {
  const { token } = useAuthContext();
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const [citations, setCitations] = useState([]); // State to store exercises fetched from backend
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [filteredCitations, setFilteredCitations] = useState([]); // State for filtered exercises
  const { darkMode } = useContext(ThemeContext);

  const updateCitation = (updatedCitation) => {
    setCitations((prevCitations) => {
      let updatedCitations;

      if (
        !updatedCitation ||
        typeof updatedCitation !== "object" ||
        !updatedCitation.id
      ) {
        // need to console.log why updatedCitation is invalid citation object
        return prevCitations;
      }

      if (updatedCitation.deleted) {
        updatedCitations = prevCitations.filter(
          (citation) => citation.id !== updatedCitation.id
        );
      } else if (
        !prevCitations.some((citation) => citation.id === updatedCitation.id)
      ) {
        updatedCitations = [...prevCitations, updatedCitation];
      } else {
        updatedCitations = prevCitations.map((citation) =>
          citation.id === updatedCitation.id ? updatedCitation : citation
        );
      }

      return updatedCitations.sort((a, b) => {
        const nameA = a?.name || "";
        const nameB = b?.name || "";
        return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
      });
    });
  };

  useEffect(() => {
    // Fetch exercises from the backend
    if (!token) return;
    const fetchCitations = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/citation`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCitations(response.data);
        setFilteredCitations(response.data); // Initialize filtered exercises
      } catch (err) {
        console.error("Error fetching citations:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCitations();
  }, [token]);

  useEffect(() => {
    // Filter exercises based on the search query
    setFilteredCitations(
      citations.filter((citation) =>
        citation.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, citations]);

  return (
    <>
      <Box
        sx={{
          pt: "60px",
          pl: 5,
          pr: 10,
        }}
      >
        <Backdrop sx={{ zIndex: 1400 }} open={loading}>
          <CircularProgress size={80} />
        </Backdrop>
        {!loading && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              sx={{ alignItems: "center", textAlign: "center", mb: 2 }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "28px",
                  color: darkMode
                    ? getColors.drawerTextDark
                    : getColors.thumbnailTextLight,
                  fontWeight: 600,
                  textAlign: "left",
                  mb: 5,
                }}
              >
                Citations Management
              </Typography>
              <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
                <TextField
                  variant="filled"
                  placeholder="Search citations"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 0 }}>
                        <SearchIcon sx={{ mt: -2, ml: 1 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={4}
              rowGap={4}
            >
              {filteredCitations.map((citation, index) => (
                <CitationItem
                  key={index}
                  {...citation}
                  onCitationUpdate={updateCitation}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default CitationPage;
